import BaseButton from '@/components/BaseButton'
import Pager from '@/components/PagerComponent'

import { OrderStatus } from '@/enums/order-status.enum'
import { OrderType } from '@/enums/order-type.enum'

import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { useVuelidate } from '@vuelidate/core'
import { maxLength, minLength, required, numeric } from '@vuelidate/validators'

import { useToast } from 'vue-toast-notification';

export default {

  components: {
    BaseButton,
    Pager
  },

  setup () {
    return { v$: useVuelidate() }
  },

  data () {
    return {
      criterias: {
        selectedDistributor: '',
        selectedCommercialProduct: '',
        selectedFunderContract: '',
        selectedBeneficiaryService: '',
        selectedCardId: '',
        selectedCardNum: '',
        selectedExpirationDate: '',
        selectedSequenceNumber: '',
        selectedOrderType: 'CARD_ORDER',
        selectedOrderStatus: '',
        selectedExpeditionReference: '',
        selectedProductionReference: ''
      },
      displayedCards: [],
      distributors: [],
      comProducts: [],
      responseErr: '',
      error: false,
      validationError: false,
      isProcessing: false,
      orderTypeEnum: OrderType,
      orderStatusEnum: OrderStatus,
      isDistributorLocked: false
    }
  },

  created () {
    if(this.$store.state.orders.invalidData) {
      const $toast = useToast();
      $toast.open({
        message: this.$i18n.t('error.' + this.$store.state.orders.invalidData),
        type:'default',
        position: 'bottom',
        duration: 5000,
        dismissible:true
      });
    }
    this.$store.dispatch('orders/resetInvalidData')
    const user = JSON.parse(sessionStorage.getItem('user'))
    this.getDistributors(user)
    this.getProducts(user)
    this.getCriterias(user)
  },

  methods: {
    search () {
      this.validationError = this.v$.$invalid

      if (!this.validationError) {
        this.$store.dispatch('orders/setCriterias', this.criterias)
        this.getOrders()
      }
    },

    async getDistributors (user) {
      this.distributors = Object.keys(user.comProductsMap).sort()
      if (user.ihmDistribRef) {
        this.criterias.selectedDistributor = user.ihmDistribRef
        this.isDistributorLocked = true
      }
    },

    async getProducts (user) {
      for (const tmpPprdcRefList of Object.values(user.comProductsMap)) {
        this.comProducts = this.comProducts.concat(Object.values(tmpPprdcRefList)[0])
      }
      this.comProducts.sort((a, b) => a.localeCompare(b))
    },

    async getCriterias (user) {
      if(this.$store.state.orders.criterias && this.$store.state.orders.orders.length > 0) {
        this.criterias = this.$store.state.orders.criterias
      }
      if (user.ihmDistribRef) { // Au cas où le distributeur serait changé
        this.criterias.selectedDistributor = user.ihmDistribRef
      }
    },

    updateComProducts () {
      const user = JSON.parse(sessionStorage.getItem('user'))
      this.comProducts = Object.values(user.comProductsMap[this.criterias.selectedDistributor])[0]
      this.comProducts.sort((a, b) => a.localeCompare(b))
    },

    async getOrders () {
      this.isProcessing = true
      this.error = false

      await this.$store.dispatch('orders/reset')
      await this.$store.dispatch('orders/search', {
        distributorRef: this.criterias.selectedDistributor,
        productRef: this.criterias.selectedCommercialProduct,
        funderContractRef: this.criterias.selectedFunderContract,
        beneficiaryServiceRef: this.criterias.selectedBeneficiaryService,
        cardId: this.criterias.selectedCardId,
        panCard: this.criterias.selectedCardNum,
        expiryDate: this.criterias.selectedExpirationDate,
        psnCard: this.criterias.selectedSequenceNumber,
        orderType: this.criterias.selectedOrderType,
        orderStatus: this.criterias.selectedOrderStatus,
        expeditionRef: this.criterias.selectedExpeditionReference,
        productionRef: this.criterias.selectedProductionReference
      }).then(
        () => {
          this.isProcessing = false
        },
        error => {
          this.isProcessing = false
          this.error = true
          this.responseErr = (error || 'BAD_REQUEST').toUpperCase()
        }
      )
    },

    formatDate (value) {
      if (value === null || value === '') {
        return value
      }
      return dayjs(value).format('YYYY/MM/DD HH:mm:ss')
    },
    formatTimestamp  (value) {
      if (value === null || value === '') {
        return value
      }
      return dayjs.unix(value).format('YYYY/MM/DD HH:mm:ss')
    },
    formatCurrency (amountInCents) {
      if (amountInCents === null || amountInCents === '') {
        return amountInCents
      }
      return new Intl.NumberFormat('fr-FR').format(amountInCents / 100)
    },

    status (validation) {
      return {
        'validation-error': validation.$invalid && this.validationError
      }
    },

    async showDetails (order) {
      this.$store.dispatch('orders/setOrder', order)
      this.$router.push({ name: 'order-detail' })
    },

    reset () {
      this.$store.dispatch('orders/reset')
      this.$store.dispatch('orders/resetCriterias')
      this.validationError = false
      this.error = false
      if (!this.isDistributorLocked) {
        this.criterias.selectedDistributor = ''
      }
      this.criterias.selectedCommercialProduct = ''
      this.criterias.selectedFunderContract = ''
      this.criterias.selectedBeneficiaryService = ''
      this.criterias.selectedCardId = ''
      this.criterias.selectedCardNum = ''
      this.criterias.selectedExpirationDate = ''
      this.criterias.selectedSequenceNumber = ''
      this.criterias.selectedOrderType = 'CARD_ORDER'
      this.criterias.selectedOrderStatus = ''
      this.criterias.selectedExpeditionReference = ''
      this.criterias.selectedProductionReference = ''
    }
  },

  validations() {
    return {
      criterias: {
        selectedDistributor: { required },
        selectedCommercialProduct: {},
        selectedFunderContract: {},
        selectedBeneficiaryService: {},
        selectedCardId: {
          maxLength: maxLength(25),
          minLength: minLength(5),
          numeric
        },
        selectedCardNum: {
          maxLength: maxLength(19),
          minLength: minLength(11),
          panMask () {
            return (
              this.criterias.selectedCardNum ? /^(([0-9]{6})([*]+)([0-9]{4}))$/.test(this.criterias.selectedCardNum) : true
            )
          }
        },
        selectedExpirationDate: {
          expDate () {
            return (
              this.criterias.selectedExpirationDate ? /^[0-9]{4}$/.test(this.criterias.selectedExpirationDate) : true
            )
          }
        },
        selectedSequenceNumber: {
          maxLength: maxLength(2),
          seqNum () {
            return (
              this.criterias.selectedSequenceNumber ? /^\d*$/.test(this.criterias.selectedSequenceNumber) : true
            )
          }
        },
        selectedOrderStatus: {},
        selectedExpeditionReference: {},
        selectedProductionReference: {}
      }
  }
  }
}
